import { useEffect, useEffectEvent, useState } from 'react';
import { Popover } from '@base-ui/react/popover';
import { Check, ChevronDown, ChevronRight, CircleCheck } from 'lucide-react';
import SmartLink from '@/shared/ui/SmartLink';
import { Avatar } from '@/modules/avatar';
import { useProfile, type Profile } from '@/routes/Providers/ProfileProvider';
import { useOnboarding } from '@/routes/Providers/OnboardingProvider';
import { track } from '@/shared/utils/track';
import { markTutorial, type TutorialKey } from '@/shared/utils/tutorials';
import { useArenaIssuer, useArenaPortfolioStakes } from '@/modules/arena-api';
import { isEligible } from '@/modules/issuers-api';
import { zHeaderNotifications } from '@/shared/constants/zIdxs';

const CHECKLIST_LABELS = {
  completeOnboarding: 'Complete onboarding',
  firstSignal: 'Build your first Alpha signal',
  firstStake: 'Back a top-tier provider',
  eligible: 'Become eligible',
};

// Arena Setup checklist. Item state lives in `issuer.tutorials` — every
// key is LATCHED via the tutorials API, deliberately including `eligible`
// (a live-data read could flip back off when accuracy drifts; a checklist
// item must never un-check). Weights are chosen so 2 done = 40% and
// 3 done = 80% — onboarding 40, everything else 20.
const CHECKLIST = [
  { key: 'onboarding_completed', copy: 'completeOnboarding', weight: 40 },
  { key: 'first_signal', copy: 'firstSignal', weight: 20 },
  { key: 'first_stake', copy: 'firstStake', weight: 20 },
  { key: 'eligible', copy: 'eligible', weight: 20 },
] as const satisfies readonly {
  key: TutorialKey;
  copy: string;
  weight: number;
}[];

// Setup-progress ring around the 32px avatar slot (wraps the 30px avatar).
const RING_R = 14;
const RING_C = 2 * Math.PI * RING_R;

// Desktop only — the Header mounts it above 767; on ≤767 the profile entry
// is the bottom nav's avatar tab, which also carries the tour's profile-pill
// anchor.
export default function ProfileMenu() {
  const { profile } = useProfile();

  if (!profile) return null;

  if (profile.tutorials?.arena_setup_finished === true) {
    return (
      <SmartLink
        className="relative z-1 flex items-center gap-xs"
        navLink
        data-onboarding="profile-pill"
        onClick={() =>
          track('nav_clicked', { location: 'header', label: 'Mine' })
        }
        to="/user/$username"
        params={{ username: profile.username }}
      >
        <span className="flex size-8 shrink-0 items-center justify-center">
          <Avatar size={30} user={profile} />
        </span>
        <div
          className="max-w-5xl truncate text-[13px] leading-[19.5px] font-medium text-gray-text"
          title={profile.username}
        >
          {profile.username}
        </div>
      </SmartLink>
    );
  }

  return <SetupPill profile={profile} />;
}

// The dropdown's open state dies by unmount on a breakpoint crossing (the
// Header drops ProfileMenu at ≤767) — widening back never restores a stale
// open dropdown (or re-fires `arena_setup_opened` through the open effect).
function SetupPill({ profile }: { profile: Profile }) {
  const { logout, updateProfile } = useProfile();
  const { reopenWelcome } = useOnboarding();
  const [open, setOpen] = useState(false);
  const [showComplete, setShowComplete] = useState(false);

  const tutorials: Record<string, boolean> = profile.tutorials ?? {};

  // Retroactive reconcile while the dropdown is open: derive what the arena
  // API already knows (pre-feature history) and latch any unmarked keys.
  // `onboarding_completed` has no derivable source — event-only.
  const { data: arenaIssuer } = useArenaIssuer(
    open ? profile.username : undefined,
  );
  // Stakes stay address-keyed (on-chain rows); a walletless profile has none.
  const { data: myStakes } = useArenaPortfolioStakes(
    open ? (profile.address ?? undefined) : undefined,
  );
  const derivedEntries: [TutorialKey, boolean][] = [
    ['first_signal', (arenaIssuer?.total_predictions ?? 0) > 0],
    ['first_stake', (myStakes?.length ?? 0) > 0],
    ['eligible', !!arenaIssuer && isEligible(arenaIssuer)],
  ];
  const derived: Partial<Record<TutorialKey, boolean>> =
    Object.fromEntries(derivedEntries);
  const derivedKey = derivedEntries
    .filter(([, v]) => v)
    .map(([k]) => k)
    .join(',');
  // done = server-latched OR freshly derived (optimistic while the latch
  // POST is in flight).
  const items = CHECKLIST.map((item) => ({
    ...item,
    done: tutorials[item.key] === true || derived[item.key] === true,
  }));
  const percent = items.reduce((sum, i) => sum + (i.done ? i.weight : 0), 0);
  const allDone = items.every((i) => i.done);

  // Reads the percent at open time without re-firing the open effect on
  // every percent change (useEffectEvent: stable identity, latest values).
  const trackOpened = useEffectEvent(() => {
    track('arena_setup_opened', { percent });
  });
  // Refresh profile.tutorials whenever the dropdown opens — event-driven
  // marks (signal create, stake mine, tour finish) POST in the background
  // and the checklist must show them without a reload.
  useEffect(() => {
    if (!open) return;
    trackOpened();
    updateProfile().catch(() => {
      // The checklist keeps its last known state; the next open refreshes.
    });
  }, [open, updateProfile]);

  // The latch must fire on reconcile-input changes only — `tutorials` is a
  // fresh object each render and its own updateProfile() refresh would
  // re-trigger a deps-complete effect, so the body reads it via an event.
  const reconcile = useEffectEvent(() => {
    const missing = derivedEntries
      .filter(([k, v]) => v && tutorials[k] !== true)
      .map(([k]) => k);
    if (missing.length === 0) return;
    void Promise.all(missing.map((k) => markTutorial(k, tutorials)))
      .then(() => updateProfile())
      .catch(() => {
        // The derived state already shows the item done; the next open
        // reconciles the latch again.
      });
  });
  useEffect(() => {
    if (!open || derivedKey === '') return;
    reconcile();
  }, [open, derivedKey]);

  return (
    <Popover.Root
      open={open}
      onOpenChange={(next, { reason, cancel }) => {
        // The completion card is closed intentionally: "Got it" or the pill
        // itself — never an outside press, Escape or focus leaving the card.
        if (!next && showComplete && reason !== 'trigger-press') {
          cancel();
          return;
        }
        setOpen(next);
      }}
    >
      <Popover.Trigger
        data-onboarding="profile-pill"
        className="btn-reset flex items-center gap-xs"
      >
        <span className="relative flex size-8 shrink-0 items-center justify-center">
          <Avatar size={30} user={profile} />
          {/* Setup-progress ring, drawn from `percent` — a fixed arc asset
              can't represent live progress. Geometry: r14, stroke 2,
              purple, 12 o'clock start. */}
          <svg
            className="absolute inset-0 -rotate-90"
            width={32}
            height={32}
            viewBox="0 0 32 32"
            aria-hidden
          >
            <circle
              cx={16}
              cy={16}
              r={RING_R}
              fill="none"
              stroke="var(--purple-default)"
              strokeWidth={2}
              strokeDasharray={`${(percent / 100) * RING_C} ${RING_C}`}
            />
          </svg>
        </span>
        <span
          className="max-w-5xl truncate text-[13px] leading-[19.5px] font-medium text-gray-text"
          title={profile.username}
        >
          {profile.username}
        </span>
        <ChevronDown size={20} className="shrink-0 text-gray-text" />
      </Popover.Trigger>

      <Popover.Portal>
        <Popover.Positioner
          side="bottom"
          align="end"
          sideOffset={8}
          style={{ zIndex: zHeaderNotifications }}
        >
          <Popover.Popup className="w-80 rounded-md bg-background-secondary p-l shadow-xl outline-none">
            {showComplete ?
              <div className="flex flex-col items-center px-s py-m text-center">
                <CircleCheck
                  size={40}
                  strokeWidth={1.5}
                  className="text-green-default"
                />
                <p className="mt-m text-body-lg font-bold text-gray-text">
                  Arena Setup Complete
                </p>
                <p className="mt-xs text-body-md text-gray">
                  Discover your profile, track your performance, and explore the
                  Arena.
                </p>
                <button
                  type="button"
                  onClick={() => {
                    setShowComplete(false);
                    setOpen(false);
                    // Now let the pill collapse to the plain profile link.
                    updateProfile().catch(() => {
                      // The pill collapses on the next refresh instead.
                    });
                  }}
                  className="mt-l btn-reset h-8 w-full rounded-sm bg-purple-default text-center text-body-md font-medium text-gray-inverse hover:bg-purple-hover"
                >
                  Got it
                </button>
              </div>
            : <>
                <SmartLink
                  to="/user/$username"
                  params={{ username: profile.username }}
                  onClick={() => setOpen(false)}
                  className="flex items-center gap-m"
                >
                  <Avatar size={40} user={profile} />
                  <div className="flex-1">
                    <p className="text-body-lg font-bold text-gray-text">
                      {profile.username}
                    </p>
                    <p className="text-body-sm text-purple-default">
                      View full profile
                    </p>
                  </div>
                  <ChevronRight size={20} className="text-gray" />
                </SmartLink>

                <div className="mt-l rounded-sm bg-purple-01/50 p-m">
                  <div className="flex items-center justify-between">
                    <span className="text-body-md font-semibold text-gray-text">
                      Arena Setup
                    </span>
                    {allDone ?
                      <button
                        type="button"
                        onClick={() => {
                          // Latch the end state NOW (Finish is the intentional
                          // completion); "Got it" only closes the card.
                          void markTutorial('arena_setup_finished', tutorials);
                          setShowComplete(true);
                        }}
                        className="btn-reset h-7 rounded-sm bg-purple-default px-m text-center text-body-md font-medium text-gray-inverse hover:bg-purple-hover"
                      >
                        Finish
                      </button>
                    : <span className="text-body-md font-semibold text-purple-default">
                        {percent}% Complete
                      </span>
                    }
                  </div>
                  <div className="mt-s h-1.5 overflow-hidden rounded-full bg-gray-5">
                    <div
                      className="h-full bg-purple-default"
                      style={{ width: `${percent}%` }}
                    />
                  </div>
                  <ul className="mt-m space-y-s">
                    {items.map((item) => (
                      <li key={item.key} className="flex items-center gap-s">
                        <span
                          className={`flex size-4 shrink-0 items-center justify-center rounded-full ${
                            item.done ?
                              'bg-purple-default text-gray-inverse'
                            : 'border border-gray-4'
                          }`}
                        >
                          {item.done && <Check className="size-3" />}
                        </span>
                        <span className="flex-1">
                          <span
                            className={`text-body-md ${
                              item.done ?
                                'text-gray line-through'
                              : 'text-gray-text'
                            }`}
                          >
                            {CHECKLIST_LABELS[item.copy]}
                          </span>
                          {item.key === 'eligible' && !item.done && (
                            <span className="block text-body-sm text-gray">
                              {'(30 signals, score range >60% or <40%)'}
                            </span>
                          )}
                        </span>
                        {item.key === 'onboarding_completed' && !item.done && (
                          <button
                            type="button"
                            onClick={() => {
                              setOpen(false);
                              reopenWelcome();
                            }}
                            className="btn-reset h-7 shrink-0 rounded-sm bg-purple-default px-m text-center text-body-md font-medium text-gray-inverse hover:bg-purple-hover"
                          >
                            Complete
                          </button>
                        )}
                      </li>
                    ))}
                  </ul>
                </div>

                <button
                  type="button"
                  onClick={() => {
                    setOpen(false);
                    logout();
                  }}
                  className="mt-l btn-reset text-body-md font-medium text-gray-text"
                >
                  Log out
                </button>
              </>
            }
          </Popover.Popup>
        </Popover.Positioner>
      </Popover.Portal>
    </Popover.Root>
  );
}
